import { useMemo } from 'react';
import type { HookFormControlShim, Lens } from '@hookform/lenses';
import { useFieldArray } from '@hookform/lenses/rhf';
import type { FieldArrayWithId, UseFieldArrayAppend } from 'react-hook-form';
import { z } from 'zod/v4';
import { Grid } from '@interxfi/design-system/components/alignment/grid';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Button } from '@interxfi/design-system/components/button/button';
import { PlainIconButton } from '@interxfi/design-system/components/button/plain-icon/plain-icon-button';
import { DropdownMenu } from '@interxfi/design-system/components/dropdown-menu/dropdown-menu';
import type { DropdownMenuItem } from '@interxfi/design-system/components/dropdown-menu/list-item/dropdown-menu-list-item';
import { INTEGER_MASK, NON_FRACTIONAL_PERCENTAGE_MASK, USD_CURRENCY_MASK_INTEGER } from '@interxfi/design-system/components/input/text-input/masks';
import { Typography } from '@interxfi/design-system/components/typography';
import DeleteIcon from '@interxfi/design-system/icons/react/icon-delete';
import PlusIcon from '@interxfi/design-system/icons/react/icon-plus';
import type { ResponsiveValue } from '@interxfi/design-system/util/responsive';
import {
  type IxcRegistrationV1Experience,
  type IxcRegistrationV1InvestmentExperience,
  type IxcRegistrationV1InvestmentProfile,
  type IxcRegistrationV1OptionsExperience,
  IxcRegistrationV1OptionsLevel,
} from '@interxfi/types/generated/api';
import { parseNumber } from '@interxfi/utilities/number/parse';
import { AppEnumSelectInputWithLens } from '../../../../../components/form/app-enum-select-input';
import { AppTextInputWithLens } from '../../../../../components/form/app-text-input';
import { addDefinedValues } from '../../../../../util/has-value';
import {
  ALLOW_EMPTY_STRING_OR_EMPTY_USD_VALUE,
  DEFAULT_USD_CURRENCY_VALIDATION_DISPLAY_FORMATTER_WITHOUT_FRACTIONALS,
  getNumericStringValidator,
} from '../../../../../util/validation';

export const investmentExperienceSectionFormSchema = z.object({
  years: getNumericStringValidator('Years', { gte: 0, lte: 100, maxDecimalPlaces: 0, required: '' }),
  transactionsPerYear: getNumericStringValidator('Transactions per Year', { gte: 0, maxDecimalPlaces: 0, required: '' }),
});

export const investmentExperienceOptionsSectionFormSchema = investmentExperienceSectionFormSchema.safeExtend({
  highestOptionStrategy: z.enum(IxcRegistrationV1OptionsLevel),
});

type InvestmentExperienceSectionFormValues = z.infer<typeof investmentExperienceSectionFormSchema>;

type OptionsInvestmentExperienceSectionFormValues = z.infer<typeof investmentExperienceOptionsSectionFormSchema>;

const equitiesExperienceSchema = z.object({ kind: z.literal('equities'), value: investmentExperienceSectionFormSchema });
const mutualFundsExperienceSchema = z.object({ kind: z.literal('mutualFunds'), value: investmentExperienceSectionFormSchema });
const bondsExperienceSchema = z.object({ kind: z.literal('bonds'), value: investmentExperienceSectionFormSchema });
const futuresExperienceSchema = z.object({ kind: z.literal('futures'), value: investmentExperienceSectionFormSchema });
const marginExperienceSchema = z.object({ kind: z.literal('margin'), value: investmentExperienceSectionFormSchema });
const optionsExperienceSchema = z.object({ kind: z.literal('options'), value: investmentExperienceOptionsSectionFormSchema });

export const investmentExperienceFormSchema = z.array(
  z.discriminatedUnion('kind', [
    equitiesExperienceSchema,
    mutualFundsExperienceSchema,
    bondsExperienceSchema,
    futuresExperienceSchema,
    marginExperienceSchema,
    optionsExperienceSchema,
  ]),
);

type InvestmentExperienceFormValues = z.infer<typeof investmentExperienceFormSchema>;

export const investmentProfileFormSchema = z.object({
  annualIncome: getNumericStringValidator('Annual Income', {
    gte: 0,
    maxDecimalPlaces: 0,
    required: true,
    displayFormatter: DEFAULT_USD_CURRENCY_VALIDATION_DISPLAY_FORMATTER_WITHOUT_FRACTIONALS,
  }),
  netWorth: getNumericStringValidator('Net Worth', {
    gte: 0,
    maxDecimalPlaces: 0,
    required: true,
    displayFormatter: DEFAULT_USD_CURRENCY_VALIDATION_DISPLAY_FORMATTER_WITHOUT_FRACTIONALS,
  }),
  liquidNetWorth: getNumericStringValidator('Liquid Net Worth', {
    gte: 0,
    maxDecimalPlaces: 0,
    required: ALLOW_EMPTY_STRING_OR_EMPTY_USD_VALUE,
    displayFormatter: DEFAULT_USD_CURRENCY_VALIDATION_DISPLAY_FORMATTER_WITHOUT_FRACTIONALS,
  }),
  usTaxRate: getNumericStringValidator('US Tax Rate', { gte: 0, lte: 100, maxDecimalPlaces: 0, required: '' }),
  investmentExperience: investmentExperienceFormSchema,
});

export type InvestmentProfileFormValues = z.infer<typeof investmentProfileFormSchema>;

const defaultInvestmentExperienceSectionProvidingFormValues: InvestmentExperienceSectionFormValues = {
  years: '',
  transactionsPerYear: '',
};

const defaultOptionInvestmentExperienceProvidingFormValues: OptionsInvestmentExperienceSectionFormValues = {
  years: '',
  transactionsPerYear: '',
  highestOptionStrategy: IxcRegistrationV1OptionsLevel.None,
};

export const defaultInvestmentProfileFormValues: InvestmentProfileFormValues = {
  annualIncome: '',
  netWorth: '',
  liquidNetWorth: '',
  usTaxRate: '',
  investmentExperience: [],
};

function getInitialFormValuesFromExperience(experience: IxcRegistrationV1Experience | undefined): InvestmentExperienceSectionFormValues {
  return {
    years: experience?.years ? String(experience.years) : '',
    transactionsPerYear: experience?.transactionsPerYear ? String(experience.transactionsPerYear) : '',
  };
}

function getInitialFormValuesFromOptionsInvestmentExperience(
  experience: IxcRegistrationV1OptionsExperience | undefined,
): OptionsInvestmentExperienceSectionFormValues {
  return {
    years: experience?.years ? String(experience.years) : '',
    transactionsPerYear: experience?.transactionsPerYear ? String(experience.transactionsPerYear) : '',
    highestOptionStrategy: experience?.highestOptionStrategy || IxcRegistrationV1OptionsLevel.None,
  };
}

function getInitialFormValuesFromInvestmentExperience(data: IxcRegistrationV1InvestmentExperience | undefined): InvestmentExperienceFormValues {
  if (!data) {
    return [];
  }

  const experienceValues: InvestmentExperienceFormValues = [];

  if (data.equities) {
    experienceValues.push({ kind: 'equities', value: getInitialFormValuesFromExperience(data.equities) });
  }

  if (data.mutualFunds) {
    experienceValues.push({ kind: 'mutualFunds', value: getInitialFormValuesFromExperience(data.mutualFunds) });
  }

  if (data.bonds) {
    experienceValues.push({ kind: 'bonds', value: getInitialFormValuesFromExperience(data.bonds) });
  }

  if (data.futures) {
    experienceValues.push({ kind: 'futures', value: getInitialFormValuesFromExperience(data.futures) });
  }

  if (data.margin) {
    experienceValues.push({ kind: 'margin', value: getInitialFormValuesFromExperience(data.margin) });
  }

  if (data.options) {
    experienceValues.push({ kind: 'options', value: getInitialFormValuesFromOptionsInvestmentExperience(data.options) });
  }

  return experienceValues;
}

export function getInitialFormValuesForInvestmentProfile(profile: IxcRegistrationV1InvestmentProfile | undefined): InvestmentProfileFormValues {
  return {
    ...defaultInvestmentProfileFormValues,
    annualIncome: profile?.annualIncome ? String(profile.annualIncome) : defaultInvestmentProfileFormValues.annualIncome,
    netWorth: profile?.netWorth ? String(profile.netWorth) : defaultInvestmentProfileFormValues.netWorth,
    liquidNetWorth: profile?.liquidNetWorth ? String(profile.liquidNetWorth) : defaultInvestmentProfileFormValues.liquidNetWorth,
    usTaxRate: profile?.usTaxRate ? String(profile.usTaxRate) : defaultInvestmentProfileFormValues.usTaxRate,
    investmentExperience: getInitialFormValuesFromInvestmentExperience(profile?.investmentExperience),
  };
}

function formValuesToInvestmentExperienceSection(
  experience: InvestmentExperienceSectionFormValues | undefined,
): IxcRegistrationV1Experience | undefined {
  if (!experience) {
    return undefined;
  }

  const years = parseNumber(experience.years);
  const transactionsPerYear = parseNumber(experience.transactionsPerYear);

  return {
    years: years ? Number(years) : 0,
    transactionsPerYear: transactionsPerYear ? Number(transactionsPerYear) : 0,
  };
}

function formValuesToOptionsInvestmentExperience(
  experience: OptionsInvestmentExperienceSectionFormValues | undefined,
): IxcRegistrationV1OptionsExperience | undefined {
  if (!experience) {
    return undefined;
  }

  return {
    years: Number(parseNumber(experience.years)),
    transactionsPerYear: Number(parseNumber(experience.transactionsPerYear)),
    highestOptionStrategy: experience.highestOptionStrategy,
  };
}

function formValuesToInvestmentExperience(values: InvestmentExperienceFormValues): IxcRegistrationV1InvestmentExperience | undefined {
  const exp = addDefinedValues<IxcRegistrationV1InvestmentExperience>(
    {},
    {
      equities: formValuesToInvestmentExperienceSection(values.find((v) => v.kind === 'equities')?.value),
      mutualFunds: formValuesToInvestmentExperienceSection(values.find((v) => v.kind === 'mutualFunds')?.value),
      bonds: formValuesToInvestmentExperienceSection(values.find((v) => v.kind === 'bonds')?.value),
      futures: formValuesToInvestmentExperienceSection(values.find((v) => v.kind === 'futures')?.value),
      margin: formValuesToInvestmentExperienceSection(values.find((v) => v.kind === 'margin')?.value),
      options: formValuesToOptionsInvestmentExperience(values.find((v) => v.kind === 'options')?.value),
    },
  );

  return Object.keys(exp).length > 0 ? exp : undefined;
}

export function formValuesToInvestmentProfile(values: InvestmentProfileFormValues): IxcRegistrationV1InvestmentProfile {
  const liquidNetWorth = parseNumber(values.liquidNetWorth);
  const usTaxRate = parseNumber(values.usTaxRate);

  return addDefinedValues<IxcRegistrationV1InvestmentProfile>(
    {
      annualIncome: Number(parseNumber(values.annualIncome)),
      netWorth: Number(parseNumber(values.netWorth)),
    },
    {
      liquidNetWorth: liquidNetWorth ? Number(liquidNetWorth) : undefined,
      usTaxRate: usTaxRate ? Number(usTaxRate) : undefined,
      investmentExperience: values.investmentExperience.length > 0 ? formValuesToInvestmentExperience(values.investmentExperience) : undefined,
    },
  );
}

const mainProfileColumns: ResponsiveValue<number> = {
  sm: 1,
  md: 2,
};

const experienceTypeLabels: Record<string, string> = {
  equities: 'Equities',
  mutualFunds: 'Mutual Funds',
  bonds: 'Bonds',
  futures: 'Futures',
  margin: 'Margin',
  options: 'Options',
};

function buildExperienceOptions(
  appendField: UseFieldArrayAppend<HookFormControlShim<InvestmentExperienceFormValues>>,
  fields: FieldArrayWithId<InvestmentExperienceFormValues>[],
): DropdownMenuItem[] {
  return (
    [
      {
        id: 'equities',
        label: experienceTypeLabels.equities,
        onClick: () => appendField({ kind: 'equities', value: defaultInvestmentExperienceSectionProvidingFormValues }),
      },
      {
        id: 'mutualFunds',
        label: experienceTypeLabels.mutualFunds,
        onClick: () => appendField({ kind: 'mutualFunds', value: defaultInvestmentExperienceSectionProvidingFormValues }),
      },
      {
        id: 'bonds',
        label: experienceTypeLabels.bonds,
        onClick: () => appendField({ kind: 'bonds', value: defaultInvestmentExperienceSectionProvidingFormValues }),
      },
      {
        id: 'futures',
        label: experienceTypeLabels.futures,
        onClick: () => appendField({ kind: 'futures', value: defaultInvestmentExperienceSectionProvidingFormValues }),
      },
      {
        id: 'margin',
        label: experienceTypeLabels.margin,
        onClick: () => appendField({ kind: 'margin', value: defaultInvestmentExperienceSectionProvidingFormValues }),
      },
      {
        id: 'options',
        label: experienceTypeLabels.options,
        onClick: () => appendField({ kind: 'options', value: defaultOptionInvestmentExperienceProvidingFormValues }),
      },
    ] as const satisfies DropdownMenuItem[]
  ).filter((f) => !fields.find((field) => 'kind' in field && field.kind === f.id));
}

const investmentExperienceColumns: ResponsiveValue<number> = {
  sm: 1,
  md: 2,
};

export interface InvestmentProfileFormProps {
  lens: Lens<InvestmentProfileFormValues>;
}

export function InvestmentProfileForm({ lens }: InvestmentProfileFormProps) {
  const experienceLens = lens.focus('investmentExperience');
  const { fields, append, remove } = useFieldArray(experienceLens.interop());
  const experienceOptions = useMemo(() => buildExperienceOptions(append, fields), [append, fields]);

  return (
    <VerticalStack gap={4}>
      <VerticalStack gap={7}>
        <Grid autoColumns={mainProfileColumns} gap={4}>
          <AppTextInputWithLens label="Annual Income" lens={lens.focus('annualIncome')} maskOptions={USD_CURRENCY_MASK_INTEGER} required />
          <AppTextInputWithLens label="Net Worth" lens={lens.focus('netWorth')} maskOptions={USD_CURRENCY_MASK_INTEGER} required />
          <AppTextInputWithLens label="Liquid Net Worth" lens={lens.focus('liquidNetWorth')} maskOptions={USD_CURRENCY_MASK_INTEGER} />
          <AppTextInputWithLens label="US Tax Rate" lens={lens.focus('usTaxRate')} maskOptions={NON_FRACTIONAL_PERCENTAGE_MASK} />
        </Grid>

        <VerticalStack gap={4}>
          <Typography.Level3 as="h3">Investment Experience</Typography.Level3>

          {experienceLens.map(fields, (field, l, i) => {
            switch (field.kind) {
              case 'equities':
              case 'bonds':
              case 'futures':
              case 'margin':
              case 'mutualFunds':
                return (
                  <VerticalStack gap={4} key={field.id}>
                    <HorizontalStack blockAlign="center" gap={4} wrap="nowrap">
                      <PlainIconButton
                        aria-label={`Remove ${experienceTypeLabels[field.kind]} investment experience`}
                        onClick={() => {
                          remove(i);
                        }}
                        tone="danger-dk"
                      >
                        <DeleteIcon aria-hidden height={16} />
                      </PlainIconButton>
                      <Typography.Level3 as="h3">{experienceTypeLabels[field.kind]}</Typography.Level3>
                    </HorizontalStack>

                    <Grid autoColumns={investmentExperienceColumns} gap={4}>
                      <AppTextInputWithLens label="Experience (In Years)" lens={l.focus('value.years')} maskOptions={INTEGER_MASK} required />
                      <AppTextInputWithLens
                        label="Transactions per Year"
                        lens={l.focus('value.transactionsPerYear')}
                        maskOptions={INTEGER_MASK}
                        required
                      />
                    </Grid>
                  </VerticalStack>
                );
              case 'options':
                return (
                  <VerticalStack gap={4} key={field.id}>
                    <HorizontalStack blockAlign="center" gap={4} wrap="nowrap">
                      <PlainIconButton
                        aria-label={`Remove ${experienceTypeLabels[field.kind]} investment experience`}
                        onClick={() => {
                          remove(i);
                        }}
                        tone="danger-dk"
                      >
                        <DeleteIcon aria-hidden height={16} />
                      </PlainIconButton>
                      <Typography.Level3 as="h3">{experienceTypeLabels[field.kind]}</Typography.Level3>
                    </HorizontalStack>

                    <VerticalStack gap={4}>
                      <Grid autoColumns={investmentExperienceColumns} gap={4}>
                        <AppTextInputWithLens label="Experience (In Years)" lens={l.focus('value.years')} maskOptions={INTEGER_MASK} required />
                        <AppTextInputWithLens
                          label="Transactions per Year"
                          lens={l.focus('value.transactionsPerYear')}
                          maskOptions={INTEGER_MASK}
                          required
                        />
                      </Grid>

                      <AppEnumSelectInputWithLens
                        enumName="IxcRegistrationV1OptionsLevel"
                        enumValue={IxcRegistrationV1OptionsLevel}
                        i18nNamespace="ixcRegistrationV1"
                        label="Highest Option Strategy"
                        lens={l.focus('value.highestOptionStrategy')}
                        sortByLabel={false}
                      />
                    </VerticalStack>
                  </VerticalStack>
                );
              default:
                return null;
            }
          })}

          {experienceOptions.length ? (
            <DropdownMenu
              activator={
                <Button affixIcon={<PlusIcon aria-hidden />} size="medium" variant="light">
                  Add Investment Experience
                </Button>
              }
              items={experienceOptions}
              label="Select Investment Experience to Add"
              labelIsHidden
            />
          ) : null}
        </VerticalStack>
      </VerticalStack>
    </VerticalStack>
  );
}
